import { z } from 'zod';
import {
  Email,
  HouseholdCode,
  HouseholdId,
  IsoDatetime,
  LangCode,
  PhoneE164,
  SignInIdentifier,
  StaffPosition,
  UserId,
  UserRole,
  Username,
} from './primitives.js';

export const Household = z.object({
  id: HouseholdId,
  name: z.string().min(1).max(120),
  family_lang: LangCode,
  primary_currency: z.string().length(3),
  timezone: z.string(),
  country_code: z.string().length(2),
  plan: z.string(),
  household_code: HouseholdCode,
  created_at: IsoDatetime,
  updated_at: IsoDatetime,
});
export type Household = z.infer<typeof Household>;

export const User = z.object({
  id: UserId,
  household_id: HouseholdId,
  role: UserRole,
  full_name: z.string().min(1).max(120),
  username: Username.nullable(),
  email: Email.nullable(),
  email_verified_at: IsoDatetime.nullable(),
  phone_e164: PhoneE164.nullable(),
  must_change_password: z.boolean(),
  preferred_lang: LangCode,
  avatar_r2_key: z.string().nullable(),
  last_seen_at: IsoDatetime.nullable(),
  // Set when the staff member completes or skips the first-time guided tour
  // on /today. NULL on first sign-in; the web client gates the overlay on
  // this being null AND role === 'staff'.
  staff_tour_seen_at: IsoDatetime.nullable(),
  disabled_at: IsoDatetime.nullable(),
  created_at: IsoDatetime,
  updated_at: IsoDatetime,
});
export type User = z.infer<typeof User>;

export const StaffProfile = z.object({
  user_id: UserId,
  household_id: HouseholdId,
  position: StaffPosition,
  hire_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable(),
  notes: z.string().nullable(),
  emergency_contact_name: z.string().nullable(),
  emergency_contact_phone: z.string().nullable(),
});
export type StaffProfile = z.infer<typeof StaffProfile>;

export const OwnerSignupRequest = z.object({
  full_name: z.string().min(1).max(120),
  email: Email,
  password: z.string().min(10).max(128),
  country: z.string().length(2),
  family_lang: LangCode,
  household_name: z.string().min(1).max(120).optional(),
  turnstile_token: z.string(),
});
export type OwnerSignupRequest = z.infer<typeof OwnerSignupRequest>;

export const OwnerSigninRequest = z.object({
  email: Email,
  password: z.string().min(1),
  // TOTP (6 digits) or a one-time backup code — both short. Capped so a
  // sign-in attempt can't push an unbounded string into the verifier.
  totp_or_otp: z.string().max(64).optional(),
});
export type OwnerSigninRequest = z.infer<typeof OwnerSigninRequest>;

export const StaffSigninRequest = z.object({
  household_code: HouseholdCode,
  // Username or phone: many workers have no email and no patience for
  // usernames — their phone is their identity. The service normalizes
  // phone-shaped input to E.164; the plain Username regex rejected '+'.
  username: z.union([Username, PhoneE164]),
  password: z.string().min(1),
});
export type StaffSigninRequest = z.infer<typeof StaffSigninRequest>;

export const UnifiedSigninRequest = z.object({
  identifier: SignInIdentifier,
  password: z.string().min(1).max(200),
  household_code: z.string().max(20).optional(),
  totp: z.string().max(20).optional(),
});
export type UnifiedSigninRequest = z.infer<typeof UnifiedSigninRequest>;

export const StaffProvisionRequest = z.object({
  full_name: z.string().min(1).max(120),
  position: StaffPosition,
  username: Username,
  password: z.string().min(10).max(128),
  preferred_lang: LangCode,
  email: Email.optional(),
  phone_e164: PhoneE164.optional(),
  emergency_contact_name: z.string().max(120).optional(),
  emergency_contact_phone: PhoneE164.optional(),
  // The day they started. Leave years run from this date, not from January,
  // so someone hired in September neither loses nine months of entitlement at
  // new year nor earns a second allowance three months in. Optional: when it
  // is missing the balance falls back to the day the account was created.
  hire_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
});
export type StaffProvisionRequest = z.infer<typeof StaffProvisionRequest>;

export const AuthTokens = z.object({
  access_token: z.string(),
  refresh_token: z.string(),
  access_token_expires_at: IsoDatetime,
});

export const SignedInResponse = z.object({
  tokens: AuthTokens,
  user: User,
  household: Household.optional(),
  must_change_password: z.boolean().optional(),
});

export const ChangePasswordRequest = z.object({
  current_password: z.string().min(1),
  new_password: z.string().min(10).max(128),
});
